import { computeResponseDue } from "@/lib/response-cycle";
import type {
  DevelopmentStatus,
  FeatureRequest,
  FeatureRequestUser,
  User,
} from "@/types";

export const MOCK_STAKEHOLDER: User = {
  id: 1,
  username: "stakeholder.demo",
  email: "stakeholder@lionparcel.local",
  display_name: "Ayu Stakeholder",
  role: "stakeholder",
  is_active: true,
};

export const MOCK_PRODUCT: User = {
  id: 2,
  username: "product.demo",
  email: "product@lionparcel.local",
  display_name: "Budi Product",
  role: "product",
  is_active: true,
};

export const MOCK_ADMIN: User = {
  id: 3,
  username: "admin.demo",
  email: "admin@lionparcel.local",
  display_name: "Dewi Admin",
  role: "admin",
  is_active: true,
};

export const MOCK_STAKEHOLDER_2: User = {
  id: 4,
  username: "citra.ops",
  email: "citra@lionparcel.local",
  display_name: "Citra Operations",
  role: "stakeholder",
  is_active: true,
};

/** All mock users shown in Admin > User management. */
export function createSeedUsers(): User[] {
  const created = (daysAgo: number) => iso(1000 * 60 * 60 * 24 * daysAgo);
  return [
    { ...MOCK_ADMIN, created_at: created(90) },
    { ...MOCK_PRODUCT, created_at: created(80) },
    { ...MOCK_STAKEHOLDER, created_at: created(60) },
    { ...MOCK_STAKEHOLDER_2, created_at: created(30) },
  ];
}

function asReqUser(u: User): FeatureRequestUser {
  return {
    id: u.id,
    username: u.username,
    display_name: u.display_name,
    role: u.role,
  };
}

const now = Date.now();
const iso = (offsetMs: number) => new Date(now - offsetMs).toISOString();

export const MOCK_WORKFLOW_STATIONS = [
  "Backlog",
  "REQUIREMENT GATHERING",
  "READY FOR GROOMING",
  "READY FOR ANALYSIS",
  "In Analysis",
  "READY TO DEVELOP",
  "In Development",
  "Ready For QA",
  "In QA",
  "Ready for Regression",
  "In Regression",
  "Ready For Release",
  "In Release",
  "Done",
] as const;

/** Seed requests covering every status for UI review. */
export function createSeedRequests(): FeatureRequest[] {
  const sh = asReqUser(MOCK_STAKEHOLDER);
  const pe = asReqUser(MOCK_PRODUCT);

  const DAY_MS = 1000 * 60 * 60 * 24;
  // Demo ages for the response cycle: one request submitted long enough ago
  // that its decision is overdue, and one whose decision lands today
  // (response days are Wed/Fri, so the age is worked out dynamically).
  const lateSubmittedDaysAgo = 13;
  const todayDate = new Date(now);
  const todayKey = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, "0")}-${String(todayDate.getDate()).padStart(2, "0")}`;
  let dueTodayDaysAgo = 1;
  for (let d = 1; d <= 7; d++) {
    if (computeResponseDue(iso(d * DAY_MS)) === todayKey) {
      dueTodayDaysAgo = d;
      break;
    }
  }

  return [
    {
      id: 1,
      title: "Bulk booking CSV upload",
      description:
        "Allow enterprise customers to upload a CSV of shipments and create bookings in one go.",
      business_justification:
        "Top 5 accounts spend hours on manual entry; this should cut ops time by ~40%.",
      project_objective: "Operational Excellence",
      priority: "high",
      directorate: "Operations",
      ceo_highlight: false,
      brd: {
        file_name: "BRD-Bulk-Booking-CSV-Upload.pdf",
        file_size: 289_540,
        content_type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        download_url: "/api/feature-requests/1/brd",
      },
      expected_deadline: iso(1000 * 60 * 60 * 24 * -21).slice(0, 10),
      requester_id: sh.id,
      requester: sh,
      status: "submitted",
      events: [
        {
          id: 1,
          actor_id: sh.id,
          actor: sh,
          from_status: "",
          to_status: "submitted",
          note: "Feature request submitted",
          created_at: iso(lateSubmittedDaysAgo * DAY_MS),
        },
      ],
      created_at: iso(lateSubmittedDaysAgo * DAY_MS),
      updated_at: iso(lateSubmittedDaysAgo * DAY_MS),
    },
    {
      id: 2,
      title: "Real-time COD settlement dashboard",
      description:
        "Show pending vs settled COD per branch with daily export.",
      business_justification:
        "Finance and network need a single source of truth for COD reconciliation.",
      project_objective: "Cost Efficiency",
      priority: "medium",
      directorate: "Finance",
      ceo_highlight: true,
      brd: {
        file_name: "BRD-COD-Settlement-Dashboard.pdf",
        file_size: 356_204,
        content_type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        download_url: "/api/feature-requests/2/brd",
      },
      expected_deadline: iso(1000 * 60 * 60 * 24 * -45).slice(0, 10),
      requester_id: sh.id,
      requester: sh,
      status: "under_review",
      product_analysis_notes: "Valid need — scoping MVP around branch + date filters.",
      reviewed_by_id: pe.id,
      reviewed_by: pe,
      reviewed_at: iso(1000 * 60 * 60 * 8),
      events: [
        {
          id: 2,
          actor_id: sh.id,
          actor: sh,
          from_status: "",
          to_status: "submitted",
          note: "Feature request submitted",
          created_at: iso(dueTodayDaysAgo * DAY_MS),
        },
        {
          id: 3,
          actor_id: pe.id,
          actor: pe,
          from_status: "submitted",
          to_status: "under_review",
          note: "Valid need — scoping MVP around branch + date filters.",
          created_at: iso(1000 * 60 * 60 * 8),
        },
      ],
      created_at: iso(dueTodayDaysAgo * DAY_MS),
      updated_at: iso(1000 * 60 * 60 * 8),
    },
    {
      id: 3,
      title: "Driver ETA push notifications",
      description:
        "Notify consignee when driver is 30 minutes away with live map deep-link.",
      business_justification:
        "Reduce failed deliveries caused by recipient not being home.",
      project_objective: "Customer Experience",
      priority: "high",
      directorate: "Customer Experience",
      ceo_highlight: true,
      important_links: [
        "https://drive.google.com/drive/folders/driver-eta-brd",
        "https://docs.google.com/presentation/d/driver-eta-deck",
        "https://docs.google.com/spreadsheets/d/driver-eta-impact",
      ],
      brd: {
        file_name: "BRD-Driver-ETA-Notifications.pdf",
        file_size: 482_113,
        content_type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        download_url: "/api/feature-requests/3/brd",
      },
      expected_deadline: iso(1000 * 60 * 60 * 24 * -14).slice(0, 10),
      requester_id: sh.id,
      requester: sh,
      status: "approved",
      product_analysis_notes: "Approved for GENESIS Q2 track.",
      reviewed_by_id: pe.id,
      reviewed_by: pe,
      reviewed_at: iso(1000 * 60 * 60 * 72),
      decided_at: iso(1000 * 60 * 60 * 72),
      jira_issue_key: "GENESIS-4201",
      jira_issue_url: "https://lionparcel.atlassian.net/browse/GENESIS-4201",
      queue_position: 1,
      target_delivery: iso(1000 * 60 * 60 * 24 * -30).slice(0, 10),
      estimated_delivery: iso(1000 * 60 * 60 * 24 * -45).slice(0, 10),
      development_status: "In Development",
      events: [
        {
          id: 4,
          actor_id: sh.id,
          actor: sh,
          from_status: "",
          to_status: "submitted",
          note: "Feature request submitted",
          created_at: iso(1000 * 60 * 60 * 120),
        },
        {
          id: 5,
          actor_id: pe.id,
          actor: pe,
          from_status: "submitted",
          to_status: "under_review",
          note: "Reviewing with CX.",
          created_at: iso(1000 * 60 * 60 * 96),
        },
        {
          id: 6,
          actor_id: pe.id,
          actor: pe,
          from_status: "under_review",
          to_status: "approved",
          note: "Approved; Jira GENESIS-4201",
          created_at: iso(1000 * 60 * 60 * 72),
        },
      ],
      created_at: iso(1000 * 60 * 60 * 120),
      updated_at: iso(1000 * 60 * 60 * 72),
    },
    {
      id: 4,
      title: "Legacy SMS gateway rewrite",
      description: "Replace vendor X SMS with in-house gateway.",
      business_justification: "Cost reduction experiment.",
      project_objective: "Revenue",
      priority: "low",
      directorate: "Sales",
      ceo_highlight: false,
      requester_id: sh.id,
      requester: sh,
      status: "rejected",
      rejection_reason:
        "Out of scope for product roadmap this half — track under infra cost program.",
      reviewed_by_id: pe.id,
      reviewed_by: pe,
      reviewed_at: iso(1000 * 60 * 60 * 12),
      decided_at: iso(1000 * 60 * 60 * 12),
      events: [
        {
          id: 7,
          actor_id: sh.id,
          actor: sh,
          from_status: "",
          to_status: "submitted",
          note: "Feature request submitted",
          created_at: iso(1000 * 60 * 60 * 36),
        },
        {
          id: 8,
          actor_id: pe.id,
          actor: pe,
          from_status: "submitted",
          to_status: "under_review",
          note: "Checking with SRE.",
          created_at: iso(1000 * 60 * 60 * 20),
        },
        {
          id: 9,
          actor_id: pe.id,
          actor: pe,
          from_status: "under_review",
          to_status: "rejected",
          note: "Out of scope for product roadmap this half — track under infra cost program.",
          created_at: iso(1000 * 60 * 60 * 12),
        },
      ],
      created_at: iso(1000 * 60 * 60 * 36),
      updated_at: iso(1000 * 60 * 60 * 12),
    },
    {
      id: 5,
      title: "Warehouse sortation heatmap",
      description: "Heatmap of sortation bottlenecks per hub and shift.",
      business_justification: "Ops leads want to rebalance shifts during peak season.",
      project_objective: "Operational Excellence",
      priority: "medium",
      directorate: "Operations",
      ceo_highlight: false,
      requester_id: sh.id,
      requester: sh,
      status: "deferred",
      product_analysis_notes:
        "Deferred — warehouse ops capacity is committed to the peak-season project until next quarter.",
      reviewed_by_id: pe.id,
      reviewed_by: pe,
      reviewed_at: iso(1000 * 60 * 60 * 72),
      decided_at: iso(1000 * 60 * 60 * 72),
      events: [
        {
          id: 10,
          actor_id: sh.id,
          actor: sh,
          from_status: "",
          to_status: "submitted",
          note: "Feature request submitted",
          created_at: iso(1000 * 60 * 60 * 24 * 6),
        },
        {
          id: 11,
          actor_id: pe.id,
          actor: pe,
          from_status: "submitted",
          to_status: "under_review",
          note: "Valid ops need; checking capacity with warehouse team.",
          created_at: iso(1000 * 60 * 60 * 24 * 4),
        },
        {
          id: 12,
          actor_id: pe.id,
          actor: pe,
          from_status: "under_review",
          to_status: "deferred",
          note: "Deferred — warehouse ops capacity is committed to the peak-season project until next quarter.",
          created_at: iso(1000 * 60 * 60 * 72),
        },
      ],
      created_at: iso(1000 * 60 * 60 * 24 * 6),
      updated_at: iso(1000 * 60 * 60 * 72),
    },
    {
      id: 6,
      title: "COD fee anomaly alerts",
      description: "Alert Finance when COD fee percentages drift outside tolerance.",
      business_justification: "Catch fee leakage earlier; currently found at month-end close.",
      project_objective: "Cost Efficiency",
      priority: "high",
      directorate: "Finance",
      ceo_highlight: false,
      requester_id: sh.id,
      requester: sh,
      status: "needs_clarification",
      product_analysis_notes:
        "Need a 30-min session with Finance to confirm anomaly thresholds and escalation owners.",
      reviewed_by_id: pe.id,
      reviewed_by: pe,
      reviewed_at: iso(1000 * 60 * 60 * 24),
      decided_at: iso(1000 * 60 * 60 * 24),
      events: [
        {
          id: 13,
          actor_id: sh.id,
          actor: sh,
          from_status: "",
          to_status: "submitted",
          note: "Feature request submitted",
          created_at: iso(1000 * 60 * 60 * 24 * 3),
        },
        {
          id: 14,
          actor_id: pe.id,
          actor: pe,
          from_status: "submitted",
          to_status: "under_review",
          note: "Promising; validating thresholds with Finance.",
          created_at: iso(1000 * 60 * 60 * 24 * 2),
        },
        {
          id: 15,
          actor_id: pe.id,
          actor: pe,
          from_status: "under_review",
          to_status: "needs_clarification",
          note: "Need a 30-min session with Finance to confirm anomaly thresholds and escalation owners.",
          created_at: iso(1000 * 60 * 60 * 24),
        },
      ],
      created_at: iso(1000 * 60 * 60 * 24 * 3),
      updated_at: iso(1000 * 60 * 60 * 24),
    },
    {
      id: 7,
      title: "Zone-based pickup scheduling",
      description: "Let branches schedule courier pickups per zone and time window.",
      business_justification: "Branches currently coordinate pickups by phone; zoning cuts missed pickups.",
      project_objective: "Operational Excellence",
      directorate: "Operations",
      ceo_highlight: false,
      expected_deadline: iso(1000 * 60 * 60 * 24 * -30).slice(0, 10),
      requester_id: MOCK_STAKEHOLDER_2.id,
      requester: asReqUser(MOCK_STAKEHOLDER_2),
      status: "approved",
      product_analysis_notes: "Approved — queued behind Driver ETA notifications.",
      reviewed_by_id: pe.id,
      reviewed_by: pe,
      reviewed_at: iso(1000 * 60 * 60 * 50),
      decided_at: iso(1000 * 60 * 60 * 50),
      jira_issue_key: "GENESIS-4202",
      jira_issue_url: "https://lionparcel.atlassian.net/browse/GENESIS-4202",
      queue_position: 2,
      development_status: "Backlog",
      events: [
        {
          id: 16,
          actor_id: MOCK_STAKEHOLDER_2.id,
          actor: asReqUser(MOCK_STAKEHOLDER_2),
          from_status: "",
          to_status: "submitted",
          note: "Feature request submitted",
          created_at: iso(1000 * 60 * 60 * 24 * 4),
        },
        {
          id: 17,
          actor_id: pe.id,
          actor: pe,
          from_status: "under_review",
          to_status: "approved",
          note: "Approved; Jira GENESIS-4202",
          created_at: iso(1000 * 60 * 60 * 50),
        },
      ],
      created_at: iso(1000 * 60 * 60 * 24 * 4),
      updated_at: iso(1000 * 60 * 60 * 50),
    },
    {
      id: 8,
      title: "Address autocomplete for booking form",
      description: "Autocomplete Indonesian addresses with postcode validation at booking.",
      business_justification: "Reduce address errors that cause failed first delivery attempts.",
      project_objective: "Customer Experience",
      directorate: "Customer Experience",
      ceo_highlight: false,
      expected_deadline: iso(1000 * 60 * 60 * 24 * 10).slice(0, 10),
      requester_id: sh.id,
      requester: sh,
      status: "approved",
      product_analysis_notes: "Approved and delivered.",
      reviewed_by_id: pe.id,
      reviewed_by: pe,
      reviewed_at: iso(1000 * 60 * 60 * 24 * 20),
      decided_at: iso(1000 * 60 * 60 * 24 * 20),
      jira_issue_key: "GENESIS-4190",
      jira_issue_url: "https://lionparcel.atlassian.net/browse/GENESIS-4190",
      development_status: "Done",
      actual_delivery: iso(1000 * 60 * 60 * 24 * 3).slice(0, 10),
      events: [
        {
          id: 18,
          actor_id: sh.id,
          actor: sh,
          from_status: "",
          to_status: "submitted",
          note: "Feature request submitted",
          created_at: iso(1000 * 60 * 60 * 24 * 25),
        },
        {
          id: 19,
          actor_id: pe.id,
          actor: pe,
          from_status: "under_review",
          to_status: "approved",
          note: "Approved; Jira GENESIS-4190",
          created_at: iso(1000 * 60 * 60 * 24 * 20),
        },
      ],
      created_at: iso(1000 * 60 * 60 * 24 * 25),
      updated_at: iso(1000 * 60 * 60 * 24 * 3),
    },
  ];
}

export function mockDevelopmentFor(
  request: FeatureRequest,
): DevelopmentStatus {
  if (!request.jira_issue_key) {
    return {
      project_status: "",
      progression_pct: 0,
      stations: [...MOCK_WORKFLOW_STATIONS],
      epics: [],
      jira_url: request.jira_issue_url ?? undefined,
    };
  }

  return {
    jira_issue_key: request.jira_issue_key,
    jira_url: request.jira_issue_url ?? undefined,
    project_status: "In Development",
    progression_pct: 46,
    stations: [...MOCK_WORKFLOW_STATIONS],
    epics: [
      {
        key: "GENESIS-4301",
        summary: "Push notification service",
        status: "In Development",
      },
      {
        key: "GENESIS-4302",
        summary: "Consignee deep-link map",
        status: "READY TO DEVELOP",
      },
    ],
  };
}
